<template>
  <span></span>
</template>
<script setup lang="ts">
import { onUnmounted, ref, watchEffect, watch } from "vue";
import Smooch from "smooch";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";

const props = defineProps({
  isInStoreDevice: {
    type: Boolean,
    required: true,
  },
  chatBotMetadata: {
    type: Object as () => Record<string, string | number | null | undefined>,
    required: true,
  },
  initializeChatBot: {
    type: Boolean,
    required: true,
  },
  integrationId: {
    type: String,
    required: true,
  },
});

const USER_CHATBOT_JWT = "PJ_CL_ChatBotJwt";
const isChatBotAuthEnabled = ref<boolean>(!props.isInStoreDevice);

const addSmooch = (params) => {
  const shouldInitializeChatBot = () => {
    return props.initializeChatBot && !isChatBotInitialized();
  };

  const isChatBotInitialized = () => {
    const iframes = document.getElementsByTagName("iframe");
    for (let i = iframes.length - 1; i >= 0; i--) {
      if (iframes[i].id === "web-messenger-container") {
        return true;
      }
    }
    return false;
  };

  const getSmoochAuthToken = async () => {
    const jwt = sessionStorage.getItem(USER_CHATBOT_JWT);
    return jwt ? Promise.resolve(jwt) : await createSunshineJwt();
  };

  const createSunshineJwt = async () => {
    const CreditLineService = new Service();
    return await CreditLineService.GetSunshineJwt({
      deviceTag: AuthenticationHelper.getDeviceTag() as string,
    })
      .then((response) => {
        if (response.data?.jwt) {
          sessionStorage.setItem(USER_CHATBOT_JWT, response.data?.jwt ?? null);
          return response.data?.jwt;
        }
        return;
      })
      .catch((error) => {
        console.log("error jwt", error);
        return;
      });
  };

  function loadSunshineConversation(conversationId: string) {
    Smooch.loadConversation(conversationId)
      .then(() => {
        console.log("Conversation Loaded");
      })
      .catch((error) => {
        console.error(error);
      });
  }

  function updateSunshineUser(
    params: Record<string, string | number | boolean>
  ) {
    Smooch.updateUser({ metadata: params })
      .then(() => {
        console.log("User updated", params);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  function hasSunshineUserDifferentUserApiToken() {
    const sunshineUser = Smooch.getUser();
    console.log({ sunshineUser }); // Temp: save to storage
    return false;
  }

  function shouldCreateNewConversation() {
    return (
      hasSunshineUserDifferentUserApiToken() && hasConversationWithMessages()
    );
  }

  function hasConversationWithMessages() {
    const conversation = Smooch.getDisplayedConversation();
    return !!conversation && !!conversation?.messages?.length;
  }

  async function createNewConversation() {
    try {
      return await Smooch.createConversation({});
    } catch (error) {
      console.error(error);
    }
  }

  const loginSunshine = async (deviceTag: string, authToken: string) => {
    Smooch.login(deviceTag, authToken).then(
      function () {
        console.log("login success");
      },
      function (error) {
        console.error(error);
      }
    );
  };

  if (shouldInitializeChatBot()) {
    if (props.integrationId === "") {
      return;
    }
    isChatBotInitialized() && Smooch.destroy();
    Smooch.init({
      integrationId: props.integrationId,
      locale: params.language,
      canUserSeeConversationList: false,
      notificationChannelPromptEnabled: false,
      browserStorage: "sessionStorage",
      integrationOrder: [],
    })
      .then(function () {
        Smooch.setDelegate({
          onInvalidAuth() {
            return new Promise((resolve) => {
              createSunshineJwt().then((authToken) => {
                resolve(authToken as string);
              });
            });
          },
        });

        if (isChatBotAuthEnabled.value) {
          getSmoochAuthToken().then((authToken) => {
            loginSunshine(params.deviceTag, authToken as string);
          });
        }

        if (!isChatBotAuthEnabled.value) {
          Smooch.updateUser({
            metadata: params,
          });
        }
      })
      .catch((error) => {
        console.log("Smooch init error:", error);
      });
  }

  Smooch.on("widget:opened", function () {
    if (isChatBotAuthEnabled.value) {
      if (shouldCreateNewConversation()) {
        createNewConversation().then((conversation) => {
          loadSunshineConversation(conversation?.id as string);
        });
      }
      updateSunshineUser(params);
    }
  });
};

watchEffect(() => {
  addSmooch(props.chatBotMetadata);
});

watch(
  () => props.initializeChatBot,
  (newVal) => {
    if (!newVal) {
      setTimeout(() => {
        Smooch.destroy();
      }, 5000);
    }
  }
);

onUnmounted(() => Smooch.destroy());
</script>
